import { useMemo } from "react";

import { EmptystateSearchNoResultsColored, Space } from "components/icons/generated";
import ListEntitiesItem from "components/ListEntitiesItem";
import ListEntitiesItemLink from "components/ListEntitiesItem/Link";
import MetaInfoListItem from "components/MetaInfoList/Item";
import SearchInput from "components/SearchInput";
import SortableTable from "components/SortableTable";
import { URL_SEARCH_KEY } from "constants/url_query_keys";
import Box from "ds/components/Box";
import EmptyState from "ds/components/EmptyState";
import useTypedContext from "hooks/useTypedContext";
import useURLParams from "hooks/useURLParams";
import { SearchQueryOrderDirection } from "types/generated";
import { fuzzySearch } from "utils/fuzzySearch";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";

import ModuleVersionPageIntoTitle from "../../components/PageIntoTitle";
import ModuleVersionScrollableWrapper from "../../components/ScrollableWrapper";
import { VersionContext } from "../../Context";
import { COLUMN_ORDER, columns, FILTERS_ORDER_SETTINGS_KEY_VERSION_CONSUMERS } from "./constants";

const ModuleVersionConsumers = () => {
  const { version } = useTypedContext(VersionContext);

  const urlParams = useURLParams();
  const searchQuery = urlParams.get(URL_SEARCH_KEY);

  const consumers = useMemo(() => {
    const filteredConsumers = version?.consumers || [];

    if (searchQuery) {
      return fuzzySearch(filteredConsumers, searchQuery.trim(), {
        keys: ["name"],
        scoreThreshold: -1000,
      });
    }

    return filteredConsumers;
  }, [version?.consumers, searchQuery]);

  return (
    <>
      <PageInfoHeaderWrapper>
        <ModuleVersionPageIntoTitle
          title="Consumers"
          tooltip="Stacks that are using this module."
        />
        <PageInfoHeaderActions>
          <SearchInput
            placeholder="Search by name..."
            filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY_VERSION_CONSUMERS}
          />
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>
      <ModuleVersionScrollableWrapper padding="0">
        {consumers.length === 0 && (
          <EmptyState
            padding="x-large"
            icon={EmptystateSearchNoResultsColored}
            caption={
              searchQuery ? "No consumers match your search query" : "This module has no consumers"
            }
            fullHeight
            announce={!!searchQuery}
          />
        )}
        {consumers.length > 0 && (
          <SortableTable
            ariaLabel="Consumers"
            items={consumers}
            columns={columns}
            columnOrder={COLUMN_ORDER}
            stickyHeader
            renderItems={(sorted) =>
              sorted.map((item) => (
                <ListEntitiesItem
                  role="row"
                  key={item.id}
                  gap="large"
                  grid
                  gridTemplate={COLUMN_ORDER}
                >
                  <Box role="rowheader">
                    <ListEntitiesItemLink href={`/stack/${item.id}`} title={item.name} />
                  </Box>
                  <Box role="cell">
                    <MetaInfoListItem
                      icon={Space}
                      linkText={item.spaceDetails.name}
                      href={`/spaces/${item.spaceDetails.id}`}
                      type="space"
                    />
                  </Box>
                </ListEntitiesItem>
              ))
            }
            initialSortBy="id"
            initialDirection={SearchQueryOrderDirection.Desc}
          />
        )}
      </ModuleVersionScrollableWrapper>
    </>
  );
};

export default ModuleVersionConsumers;
